import { Flex } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { ContractOverviewFigure, type ContractReportQueryDto } from "../../../autogen/bff-api";
import { displayDate } from "../../../common/formatting/displayDate";
import { RemovableFilterChip } from "../../../common/components/RemovableFilterChip";
import { figureLabel } from "../../overview/contractFigures";

type OverviewFilterKey = keyof Pick<
  ContractReportQueryDto,
  | "withoutActiveOwner"
  | "activeFrameworkAgreement"
  | "missingRequiredData"
  | "withAnnualValueIn"
  | "awaitingSignature"
  | "signedFrom"
  | "signedTo"
  | "optionDeadlineFrom"
  | "optionDeadlineTo"
  | "optionDecisionDue"
  | "optionDecisionWithoutDeadline"
>;

export const overviewFilterChips = (
  t: TFunction,
  query: ContractReportQueryDto,
): FilterChip<OverviewFilterKey>[] => {
  const period = (from?: string | null, to?: string | null) =>
    [from, to].map((date) => (date ? displayDate(date) : "…")).join("–");

  return [
    ...(query.activeFrameworkAgreement
      ? [{ label: t("In an active framework agreement"), keys: ["activeFrameworkAgreement" as const] }]
      : []),
    ...(query.withoutActiveOwner
      ? [{ label: figureLabel(t, ContractOverviewFigure.WithoutActiveOwner), keys: ["withoutActiveOwner" as const] }]
      : []),
    ...(query.missingRequiredData
      ? [{ label: t("Lacks an end date, a value or a responsible person"), keys: ["missingRequiredData" as const] }]
      : []),
    ...(query.withAnnualValueIn
      ? [
          {
            label: t("Has an annual value in {{currency}}", { currency: query.withAnnualValueIn }),
            keys: ["withAnnualValueIn" as const],
          },
        ]
      : []),
    ...(query.awaitingSignature
      ? [{ label: figureLabel(t, ContractOverviewFigure.AwaitingSignature), keys: ["awaitingSignature" as const] }]
      : []),
    ...(query.signedFrom || query.signedTo
      ? [
          {
            label: t("Signed {{period}}", { period: period(query.signedFrom, query.signedTo) }),
            keys: ["signedFrom" as const, "signedTo" as const],
          },
        ]
      : []),
    ...(query.optionDeadlineFrom || query.optionDeadlineTo
      ? [
          {
            label: t("Option deadline {{period}}", { period: period(query.optionDeadlineFrom, query.optionDeadlineTo) }),
            keys: ["optionDeadlineFrom" as const, "optionDeadlineTo" as const],
          },
        ]
      : []),
    ...(query.optionDecisionDue
      ? [{ label: t("An option awaits a decision"), keys: ["optionDecisionDue" as const] }]
      : []),
    ...(query.optionDecisionWithoutDeadline
      ? [
          {
            label: t("An option awaits a decision, with no deadline set"),
            keys: ["optionDecisionWithoutDeadline" as const],
          },
        ]
      : []),
  ];
};

export interface FilterChip<K extends keyof ContractReportQueryDto> {
  label: string;
  keys: K[];
}

export const FilterChips = <K extends keyof ContractReportQueryDto>({
  chips,
  onClear,
  testId,
}: {
  chips: FilterChip<K>[];
  onClear: (keys: K[]) => void;
  testId: string;
}) => {
  if (chips.length === 0) return null;

  return (
    <Flex gap="2" rowGap="3" mb="3" wrap="wrap" data-testid={testId}>
      {chips.map((chip) => (
        <RemovableFilterChip key={chip.label} label={chip.label} onRemove={() => onClear(chip.keys)} />
      ))}
    </Flex>
  );
};
